import { fg } from '@atlaskit/platform-feature-flags/fg';
import { logException } from '@atlassian/conversation-assistant-instrumentation/services/sentry';

import { StoreErrorFlagId } from '../../common/ui/hooks/use-show-store-error-flag/StoreErrorFlagId';
import { getConversationHydrationCoordinator } from './conversationHydrationCoordinator';
import { initializeConversationData } from './initializeConversationData';
import { mergeConversationWithNetworkPriority } from './mergeConversationWithNetworkPriority';
import type { ChatStoreAction } from './types';

export type EnsureConversationArgs = Readonly<{
	conversationId: string;
	showErrorOnFailure?: boolean;
}>;

/**
 * Ensures a singular AGG conversation has a normal store entry without loading its messages.
 * Concurrent callers in the same site/account scope share the metadata request.
 */
export const ensureConversation =
	({
		conversationId,
		showErrorOnFailure = false,
	}: EnsureConversationArgs): ChatStoreAction<Promise<boolean>> =>
	async (
		{ dispatch, getState },
		{ assistanceService, getConversationHydrationScopeKey, showFlag, siteId },
	) => {
		if (!fg('rovo-chat-ai-inbox')) {
			return false;
		}

		if (getState().conversations[conversationId]) {
			return true;
		}

		const requestScopeKey = getConversationHydrationScopeKey?.();
		if (requestScopeKey == null) {
			return false;
		}
		const conversationHydrationCoordinator = getConversationHydrationCoordinator(
			assistanceService,
			requestScopeKey,
		);

		const existingRequest = conversationHydrationCoordinator.getPending(conversationId);
		const requestSiteId = siteId;
		const ownsRequest = existingRequest == null;
		const request =
			existingRequest ??
			(async () => {
				const metadata = await assistanceService.fetchConversationMetadata(conversationId);
				const hydratedSiteId = getState().hydratedSiteId;

				if (
					getConversationHydrationScopeKey?.() !== requestScopeKey ||
					(hydratedSiteId !== undefined && hydratedSiteId !== requestSiteId) ||
					conversationHydrationCoordinator.wasRemovedWhilePending(conversationId)
				) {
					return false;
				}

				// Another store path populated the conversation while metadata was pending. Its
				// local messages, draft and newer navigation data are authoritative.
				if (getState().conversations[conversationId]) {
					return true;
				}

				if (metadata.id !== conversationId) {
					return false;
				}

				dispatch(
					initializeConversationData({
						conversationId,
						data: mergeConversationWithNetworkPriority(metadata),
					}),
				);

				return getState().conversations[conversationId] != null;
			})();

		if (ownsRequest) {
			conversationHydrationCoordinator.setPending(conversationId, request);
		}

		try {
			return await request;
		} catch (error: unknown) {
			if (ownsRequest) {
				void logException({
					name: 'ensureConversationMetadata',
					exception: error,
					tags: { conversationId, scopeKey: conversationHydrationCoordinator.scopeKey },
				});
			}
			if (showErrorOnFailure) {
				showFlag?.(StoreErrorFlagId.CONVERSATION_HISTORY_LOAD_FAILED);
			}
			return false;
		} finally {
			if (ownsRequest) {
				conversationHydrationCoordinator.clearPending(conversationId, request);
			}
		}
	};
